import { useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Checkbox, Input, Modal, Select, message } from 'antd';
import Layout from '../../Layout';
import { hrApi, useHr, type EmployeeDraft } from './hrStore';
import {
  DOC_KEYS, EDUCATION, SOURCE, empById, empProbationLimit, isWorking, positionById, requiredDocs, toDay,
  type DocKey, type Education, type Employee, type Source,
} from './hrLogic';
import { Field, PageHeader, empUrl, useHrPerms } from './hrUi';
import { DateInput } from './hrModals';

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 shadow-sm">
    <div className="font-semibold text-sm mb-3">{title}</div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">{children}</div>
  </div>
);

function Form() {
  const s = useHr();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { me } = useHrPerms();
  const today = toDay();
  const cur = empById(s, params.get('id') || undefined);
  const [d, setD] = useState<EmployeeDraft>(() => cur ? { ...cur } : {
    name: '', gender: 'Nữ', phone: '', branchId: params.get('branch') || '', positionId: '', education: 'college' as Education,
    hireDate: today, source: 'referral' as Source, docs: {},
  });
  const patch = (p: Partial<EmployeeDraft>) => setD(x => ({ ...x, ...p }));
  const pos = positionById(s, d.positionId);
  const managers = s.employees.filter(e => e.id !== cur?.id && isWorking(e, today) && positionById(s, e.positionId)?.level !== 'staff');
  const docKeys = pos ? requiredDocs(s, { ...(d as Employee), docs: d.docs || {} }) : [];
  const lim = pos ? empProbationLimit(s, d as Employee) : null;

  const save = () => {
    const r = hrApi.saveEmployee(d, me);
    if (!r.ok) { Modal.error({ title: 'Chưa lưu được', content: r.errors?.join('\n') }); return; }
    message.success(cur ? 'Đã cập nhật hồ sơ' : 'Đã tạo hồ sơ — lập thỏa thuận thử việc / hợp đồng');
    navigate(cur ? empUrl(r.id!) : `${empUrl(r.id!, 'contracts')}&newContract=1`, { replace: true });
  };

  return (
    <div className="flex-1 flex flex-col min-w-0 gap-4 pb-20">
      <PageHeader title={cur ? `Sửa hồ sơ · ${cur.name}` : 'Thêm nhân sự'} desc={cur ? `${cur.code}` : 'Nhân sự tuyển qua quy trình tuyển dụng được tạo tự động khi bấm Nhận việc — dùng màn này cho nhân sự chuyển về / nhập hồ sơ cũ'} />
      <Section title="Thông tin cá nhân">
        <Field label="Họ và tên *"><Input value={d.name} onChange={x => patch({ name: x.target.value })} /></Field>
        <Field label="Giới tính"><Select className="w-full" value={d.gender} onChange={gender => patch({ gender })} options={[{ value: 'Nữ', label: 'Nữ' }, { value: 'Nam', label: 'Nam' }]} /></Field>
        <Field label="Ngày sinh"><DateInput value={d.dob} onChange={dob => patch({ dob: dob || undefined })} /></Field>
        <Field label="Số điện thoại *"><Input value={d.phone} onChange={x => patch({ phone: x.target.value })} /></Field>
        <Field label="Email"><Input value={d.email} onChange={x => patch({ email: x.target.value || undefined })} /></Field>
        <Field label="Số CCCD"><Input value={d.cccd} onChange={x => patch({ cccd: x.target.value || undefined })} /></Field>
      </Section>
      <Section title="Công việc">
        <Field label="Chi nhánh *"><Select className="w-full" value={d.branchId || undefined} placeholder="Chọn chi nhánh" onChange={branchId => patch({ branchId })} options={s.branches.map(b => ({ value: b.id, label: b.name }))} /></Field>
        <Field label="Vị trí *" hint={pos?.license ? 'Vị trí bắt buộc có giấy phép hành nghề / chứng chỉ' : undefined}>
          <Select showSearch className="w-full" value={d.positionId || undefined} placeholder="Chọn vị trí" optionFilterProp="label" onChange={positionId => patch({ positionId })}
            options={s.depts.map(dp => ({ label: dp.name, options: s.positions.filter(p => p.deptId === dp.id).map(p => ({ value: p.id, label: p.name })) }))} />
        </Field>
        <Field label="Trình độ" hint={lim?.label}>
          <Select className="w-full" value={d.education} onChange={education => patch({ education })} options={(Object.keys(EDUCATION) as Education[]).map(k => ({ value: k, label: EDUCATION[k] }))} />
        </Field>
        <Field label="Ngày vào làm *"><DateInput value={d.hireDate} onChange={hireDate => patch({ hireDate })} /></Field>
        <Field label="Quản lý trực tiếp">
          <Select showSearch allowClear className="w-full" value={d.managerId} optionFilterProp="label" onChange={managerId => patch({ managerId })}
            options={managers.map(e => ({ value: e.id, label: `${e.name} · ${positionById(s, e.positionId)?.name}` }))} />
        </Field>
        <Field label="Nguồn tuyển"><Select className="w-full" value={d.source} onChange={source => patch({ source })} options={(Object.keys(SOURCE) as Source[]).map(k => ({ value: k, label: SOURCE[k] }))} /></Field>
      </Section>
      {docKeys.length > 0 && (
        <Section title="Giấy tờ đã nhận">
          {docKeys.map((k: DocKey) => (
            <Checkbox key={k} checked={!!d.docs?.[k]} onChange={x => patch({ docs: { ...d.docs, [k]: x.target.checked } })}>{DOC_KEYS[k]}</Checkbox>
          ))}
        </Section>
      )}
      <div className="sticky bottom-0 z-10 -mx-1 px-4 py-3 bg-white/95 dark:bg-gray-900/95 border border-gray-200 dark:border-gray-800 rounded-xl shadow flex justify-end gap-2">
        <Button onClick={() => navigate(-1)}>Hủy</Button>
        <Button type="primary" onClick={save}>{cur ? 'Lưu thay đổi' : 'Tạo hồ sơ'}</Button>
      </div>
    </div>
  );
}

/** Thêm / sửa hồ sơ nhân sự — form được dựng lại khi đổi query (?id=) */
export default function EmployeeForm() {
  const loc = useLocation();
  return <Layout><Form key={loc.search} /></Layout>;
}
